import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'

export type ThemeMode = 'system' | 'light' | 'dark'
type ResolvedTheme = 'light' | 'dark'

interface ThemeContextType {
  mode: ThemeMode
  resolvedTheme: ResolvedTheme
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = (): ThemeContextType => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

export const THEME_STORAGE_KEY = 'dalify-theme'

// Mirrors the inline bootstrap script in index.html — keep both in sync.
const getStoredMode = (): ThemeMode => {
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  return stored === 'dark' || stored === 'light' ? stored : 'system'
}

const getSystemPrefersDark = (): boolean => window.matchMedia('(prefers-color-scheme: dark)').matches

const resolve = (mode: ThemeMode): ResolvedTheme =>
  mode === 'system' ? (getSystemPrefersDark() ? 'dark' : 'light') : mode

const applyClass = (resolved: ResolvedTheme): void => {
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolve(mode))

  // Safety net: reconcile the DOM class with React state once on mount, in case
  // the bootstrap script's read ever drifts from this file's (e.g. a corrupted
  // localStorage value, or the two falling out of sync in a future edit).
  useEffect(() => {
    applyClass(resolvedTheme)
  }, [])

  useEffect(() => {
    if (mode !== 'system') return

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (): void => {
      const next: ResolvedTheme = media.matches ? 'dark' : 'light'
      setResolvedTheme(next)
      applyClass(next)
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [mode])

  const setMode = (next: ThemeMode): void => {
    if (next === 'system') {
      localStorage.removeItem(THEME_STORAGE_KEY)
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    }
    const nextResolved = resolve(next)
    setModeState(next)
    setResolvedTheme(nextResolved)
    applyClass(nextResolved)
  }

  return <ThemeContext.Provider value={{ mode, resolvedTheme, setMode }}>{children}</ThemeContext.Provider>
}
